import { Fragment, useMemo } from "react";
import { Box, Flex, Image, Link, Text } from "@chakra-ui/react";
import { Avatar } from "@components";
import { useMeeting, useSdkUtils } from "@hooks";
import { formattedDate } from "@utils/helpers";
import linkifyText from "@utils/linkifyText";
import hostIndicatorIcon from "@assets/svg/host-indicator.svg";
import coHostIndicatorIcon from "@assets/svg/cohost-indicator.svg";

const failedCaptionForError = (errorCode) =>
  errorCode === "RATE_LIMITED"
    ? "Sending too fast — try again shortly"
    : "Failed to send";

const MessageText = ({ text, isDimmed }) => {
  const { openUrl } = useSdkUtils();
  const segments = useMemo(() => linkifyText(text), [text]);

  return (
    <Text
      fontSize="12px"
      color="gray.25"
      lineHeight="1.5"
      opacity={isDimmed ? 0.5 : 1}
      sx={{ overflowWrap: "anywhere" }}
    >
      {segments.map((segment, index) =>
        segment.type === "link" ? (
          <Link
            key={index}
            color="link"
            href={segment.href}
            onClick={(event) => {
              event.preventDefault();
              openUrl(segment.href);
            }}
            sx={{ overflowWrap: "anywhere" }}
          >
            {segment.value}
          </Link>
        ) : (
          <Fragment key={index}>{segment.value}</Fragment>
        )
      )}
    </Text>
  );
};

const FailedCaption = ({ errorCode, onRetry, onDiscard }) => {
  return (
    <Flex align="center" gap="8px" mt="2px" fontSize="10px">
      <Text color="red.400">{failedCaptionForError(errorCode)}</Text>
      <Text color="red.400" fontWeight="600" cursor="pointer" onClick={onRetry}>
        Retry
      </Text>
      <Text
        color="red.400"
        fontWeight="600"
        cursor="pointer"
        onClick={onDiscard}
      >
        Discard
      </Text>
    </Flex>
  );
};

const ChatMessageRow = ({
  message,
  isGrouped,
  profile,
  onRetry,
  onDiscard,
}) => {
  const { isHost, isHostOrCoHost } = useMeeting();
  const isOwn = message.isOwn;
  const senderName = isOwn
    ? profile?.username || profile?.firstName
    : message.senderName;
  const senderColor = isOwn ? profile?.color : message.senderColor;
  const senderAvatar = isOwn ? profile?.avatar : message.senderAvatar;
  const isFailed = message.status === "failed";

  const messageBody = (
    <>
      <MessageText text={message.text} isDimmed={isFailed} />
      {isFailed && (
        <FailedCaption
          errorCode={message.errorCode}
          onRetry={() => onRetry(message)}
          onDiscard={() => onDiscard(message.clientMessageId)}
        />
      )}
    </>
  );

  if (isGrouped) {
    return (
      <Box pl="48px" mt="-4px">
        {messageBody}
      </Box>
    );
  }

  return (
    <Flex gap="8px" align="flex-start">
      <Avatar size="sm" color={senderColor} avatar={senderAvatar} />
      <Box minW="0" flex="1">
        <Flex align="center" gap="6px">
          <Text
            fontSize="12px"
            fontWeight="600"
            color={senderColor}
            whiteSpace="nowrap"
          >
            {senderName}
          </Text>
          {isHostOrCoHost(message.senderId) && (
            <Image
              src={
                isHost(message.senderId)
                  ? hostIndicatorIcon
                  : coHostIndicatorIcon
              }
              boxSize="12px"
            />
          )}
          {isOwn && (
            <Text fontSize="10px" color="dark.200">
              (you)
            </Text>
          )}
          <Text fontSize="10px" color="dark.200" whiteSpace="nowrap">
            {formattedDate(message.sentAt, "h:mm A")}
          </Text>
        </Flex>
        {messageBody}
      </Box>
    </Flex>
  );
};

export default ChatMessageRow;
